import { useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react"
import { SettingsLayout } from "../layout"
import { api_fetch, api_fetch_throws } from "@/api"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { toast } from "@/components/ui/use-toast"
import { usePageTitle } from "@/utils"
import type { RuleChange, RuleMapping } from "../reason_code_mapping"
import { MakerFilter, ShowFilter, describe_match, filter_rules, gl_label, maker_label, review_flags, rule_timeline } from "./describe"

/**
 * Coding rules review: every category → GL account rule as one sentence, with who made it, what
 * it reaches, and its change history. Rules are made automatically — by a person coding a
 * deduction, or by CodingWiz — and a person's later edit re-points the rule, so this page is where
 * a team checks what the system has learned.
 */
export function SettingsCodingRulesPage() {
  usePageTitle("Coding rules")
  const { data: rules, mutate } = useSWR("coding-rules", () => api_fetch_throws<RuleMapping[]>("/accounting/rules", { use_cache: false }))
  const [search, setSearch] = useState("")
  const [maker, setMaker] = useState<MakerFilter>("all")
  const [show, setShow] = useState<ShowFilter>("all")

  const visible = useMemo(() => filter_rules(rules ?? [], search, maker, show), [rules, search, maker, show])

  async function set_enabled(rule: RuleMapping, enabled: boolean) {
    const res = await api_fetch<RuleMapping>(`/accounting/rules/${rule.id}`, { method: "PUT", body: { enabled } })
    if (!res.ok) {
      toast({ title: "Could not update rule", description: res.error.message, variant: "destructive" })
      return
    }
    void mutate((prev: RuleMapping[] | undefined) => prev?.map((r: RuleMapping) => (r.id === rule.id ? res.value.data : r)), { revalidate: false })
  }

  return (
    <SettingsLayout>
      <div class="bg-white rounded-lg shadow-sm p-6 space-y-5">
        <div>
          <h2 class="text-lg font-semibold leading-7 text-gray-900">Coding rules</h2>
          <p class="text-sm text-gray-500 max-w-3xl">
            Each rule codes a deduction category to a GL account. Rules are made automatically: when someone codes a deduction, or when
            CodingWiz does. If someone recodes a deduction later, the rule follows their edit. Turn a rule off to stop it auto-coding.
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <Input
            className="w-72"
            placeholder="Search category, invoice code or GL…"
            value={search}
            onInput={(e: Event) => setSearch((e.target as HTMLInputElement).value)}
          />
          <div class="w-44">
            <Select value={maker} onValueChange={(v: string) => setMaker(v as MakerFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Made by anyone</SelectItem>
                <SelectItem value="person">Made by a person</SelectItem>
                <SelectItem value="codingwiz">Made by CodingWiz</SelectItem>
                <SelectItem value="imported">Imported</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="w-44">
            <Select value={show} onValueChange={(v: string) => setShow(v as ShowFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rules</SelectItem>
                <SelectItem value="needs_review">Worth a look</SelectItem>
                <SelectItem value="off">Turned off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span class="self-center text-sm text-gray-500">
            {visible.length} of {rules?.length ?? 0}
          </span>
        </div>

        {!rules ? (
          <div class="text-sm text-gray-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div class="text-sm text-gray-500">No rules match.</div>
        ) : (
          <ul class="divide-y divide-gray-200 border rounded-md">
            {visible.map((rule: RuleMapping) => (
              <CodingRuleRow key={rule.id} rule={rule} onToggle={(enabled: boolean) => void set_enabled(rule, enabled)} />
            ))}
          </ul>
        )}
      </div>
    </SettingsLayout>
  )
}

function short_date(iso: string | null): string {
  return iso ? format(parseISO(iso), "MMM d, yyyy") : ""
}

interface CodingRuleRowProps {
  rule: RuleMapping
  onToggle: (enabled: boolean) => void
}

function CodingRuleRow({ rule, onToggle }: CodingRuleRowProps) {
  const [open, setOpen] = useState(false)
  const flags = review_flags(rule)
  const { changes, created } = rule_timeline(rule.history)
  const last = changes.length > (created ? 1 : 0) ? changes[changes.length - 1] : null

  return (
    <li class={rule.enabled ? "p-4" : "p-4 bg-gray-50"}>
      <div class="flex items-start gap-4">
        <Switch checked={rule.enabled} onCheckedChange={onToggle} className="mt-0.5" aria-label="Rule on" />
        <div class="min-w-0 flex-1 space-y-1">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span class="font-medium text-gray-900">{rule.category_name ?? "No category"}</span>
            {rule.category_description ? <span class="text-gray-600">{rule.category_description}</span> : null}
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <span class="text-gray-900">{gl_label(rule)}</span>
            {flags.map((f: string) => (
              <span key={f} class="text-[11px] rounded-full bg-amber-50 text-amber-800 px-2 py-0.5">
                {f}
              </span>
            ))}
          </div>
          <div class="text-xs text-gray-600">
            {rule.category_source ? <span class="uppercase tracking-wide text-gray-400 mr-1">{rule.category_source}</span> : null}
            {describe_match(rule)}
            {rule.example_invoice_numbers.length > 0 ? (
              <span class="text-gray-400"> · e.g. {rule.example_invoice_numbers.join(", ")}</span>
            ) : null}
          </div>
          <div class="text-xs text-gray-500">
            Made by {maker_label(created?.actor ?? rule.actor, created?.actor_email ?? rule.actor_email)}
            {` on ${short_date(created?.changed_at ?? rule.created_at)}`}
            {last ? ` · last changed by ${maker_label(last.actor, last.actor_email)} on ${short_date(last.changed_at)}` : null}
            {` · ${rule.category_deduction_count} deduction${rule.category_deduction_count === 1 ? "" : "s"} in this category`}
            {rule.coded_count > 0 ? ` · auto-coded ${rule.coded_count}` : null}
          </div>
          {changes.length > 1 || (changes.length === 1 && !created) ? (
            <button class="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800" onClick={() => setOpen(!open)}>
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              History ({changes.length})
            </button>
          ) : null}
          {open ? <RuleHistory history={changes} /> : null}
        </div>
      </div>
    </li>
  )
}

function RuleHistory({ history }: { history: RuleChange[] }) {
  return (
    <ol class="mt-1 space-y-0.5 text-xs text-gray-600 border-l pl-3">
      {history.map((h: RuleChange, i: number) => (
        <li key={i}>
          <span class="text-gray-400">{short_date(h.changed_at)}</span> {maker_label(h.actor, h.actor_email)}{" "}
          {h.old_gl_account ? (
            <>
              changed <span class="font-mono">{h.old_gl_account}</span> → <span class="font-mono">{h.new_gl_account}</span>
            </>
          ) : (
            <>
              created it on <span class="font-mono">{h.new_gl_account}</span>
            </>
          )}
        </li>
      ))}
    </ol>
  )
}
